/**
 * Enhanced Patient History Page
 * Premium UX, Production-Grade, Therapist/Psychiatrist Focused
 * Comprehensive treatment history with timeline, charts, and progress tracking
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Tooltip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Paper,
  Divider,
  Stack,
  LinearProgress,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  BarChart as BarChartIcon,
  TrendingUp,
  TrendingDown,
  FilterList,
  Download,
  Refresh,
  CalendarToday,
  Medication,
  Psychology,
  LocalHospital,
  Assessment,
  Description,
  Event,
  CheckCircle,
  Warning,
  Schedule,
  History,
  Science,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { styled } from '@mui/material/styles';
import { format, parseISO, subDays, subMonths, isAfter, isBefore } from 'date-fns';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { getPatientDetails } from '../../../features/patients/patientSlice';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: alpha(theme.palette.background.paper, 0.95),
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const TimelineContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(2),
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 30,
    top: 0,
    bottom: 0,
    width: 2,
    background: `linear-gradient(to bottom, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.3)})`,
  },
}));

const TimelineItem = styled(Box)(({ theme, type }) => {
  const colors = {
    consultation: theme.palette.primary.main,
    medication: theme.palette.info.main,
    assessment: theme.palette.warning.main,
    lab: theme.palette.success.main,
    note: theme.palette.secondary.main,
    milestone: theme.palette.error.main,
  };
  const color = colors[type] || theme.palette.grey[500];

  return {
    position: 'relative',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(6),
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 22,
      top: theme.spacing(2.5),
      width: 16,
      height: 16,
      borderRadius: '50%',
      backgroundColor: color,
      border: `3px solid ${theme.palette.background.paper}`,
      zIndex: 1,
    },
  };
});

const EnhancedPatientHistory = ({ patientId: propPatientId, role = 'doctor' }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { id: paramPatientId } = useParams();
  const finalPatientId = propPatientId || paramPatientId;

  const patientsState = useSelector((state) => state.patients);
  const patient = patientsState?.selectedPatient;
  const loading = patientsState?.selectedLoading || false;
  const error = patientsState?.selectedError;

  // Local state
  const [tabValue, setTabValue] = useState(0);
  const [dateRange, setDateRange] = useState('all'); // all, 30d, 90d, 6m, 1y
  const [filterType, setFilterType] = useState('all'); // all, consultation, medication, assessment, lab
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch patient data
  useEffect(() => {
    if (finalPatientId) {
      dispatch(getPatientDetails(finalPatientId));
    }
  }, [dispatch, finalPatientId]);

  // Mock data structure - replace with actual API calls
  const historyData = useMemo(() => {
    // This should come from API
    const consultations = patient?.appointments || [];
    const medications = patient?.prescriptions || [];
    const assessments = patient?.assessments || [];
    const notes = patient?.notes || [];

    // Combine and sort by date
    const allItems = [
      ...consultations.map(item => ({ ...item, type: 'consultation', date: item.scheduled_date || item.date })),
      ...medications.map(item => ({ ...item, type: 'medication', date: item.prescribed_date || item.date })),
      ...assessments.map(item => ({ ...item, type: 'assessment', date: item.assessment_date || item.date })),
      ...notes.map(item => ({ ...item, type: 'note', date: item.created_at || item.date })),
    ].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA; // Most recent first
    });

    // Apply filters
    let filtered = allItems;

    // Date range filter
    if (dateRange !== 'all') {
      const now = new Date();
      let cutoffDate;
      switch (dateRange) {
        case '30d':
          cutoffDate = subDays(now, 30);
          break;
        case '90d':
          cutoffDate = subDays(now, 90);
          break;
        case '6m':
          cutoffDate = subMonths(now, 6);
          break;
        case '1y':
          cutoffDate = subMonths(now, 12);
          break;
        default:
          cutoffDate = null;
      }
      if (cutoffDate) {
        filtered = filtered.filter(item => isAfter(new Date(item.date), cutoffDate));
      }
    }

    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(item => item.type === filterType);
    }

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(item => {
        const searchableText = [
          item.title || '',
          item.description || '',
          item.reason || '',
          item.medication_name || '',
          item.diagnosis || '',
        ].join(' ').toLowerCase();
        return searchableText.includes(searchLower);
      });
    }

    return filtered;
  }, [patient, dateRange, filterType, searchTerm]);

  // Calculate statistics
  const stats = useMemo(() => {
    const consultations = historyData.filter(item => item.type === 'consultation');
    const medications = historyData.filter(item => item.type === 'medication');
    const assessments = historyData.filter(item => item.type === 'assessment');
    const notes = historyData.filter(item => item.type === 'note');

    // Calculate progress trends
    const last30Days = historyData.filter(item => {
      const itemDate = new Date(item.date);
      return isAfter(itemDate, subDays(new Date(), 30));
    });

    return {
      totalItems: historyData.length,
      consultations: consultations.length,
      medications: medications.length,
      assessments: assessments.length,
      notes: notes.length,
      last30Days: last30Days.length,
      trend: last30Days.length > 5 ? 'up' : last30Days.length < 3 ? 'down' : 'stable',
    };
  }, [historyData]);

  // Prepare chart data
  const chartData = useMemo(() => {
    const monthlyData = {};
    historyData.forEach(item => {
      const date = new Date(item.date);
      const monthKey = format(date, 'MMM yyyy');
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { month: monthKey, consultations: 0, medications: 0, assessments: 0 };
      }
      if (item.type === 'consultation') monthlyData[monthKey].consultations++;
      if (item.type === 'medication') monthlyData[monthKey].medications++;
      if (item.type === 'assessment') monthlyData[monthKey].assessments++;
    });
    return Object.values(monthlyData).sort((a, b) => {
      return new Date(a.month) - new Date(b.month);
    }).slice(-12); // Last 12 months
  }, [historyData]);

  const handleExport = () => {
    // Export functionality
    console.log('Exporting patient history...');
  };

  const getTypeIcon = (type) => {
    const icons = {
      consultation: <Psychology />,
      medication: <Medication />,
      assessment: <Assessment />,
      lab: <Science />,
      note: <Description />,
      milestone: <CheckCircle />,
    };
    return icons[type] || <Event />;
  };

  const getTypeColor = (type) => {
    const colors = {
      consultation: 'primary',
      medication: 'info',
      assessment: 'warning',
      lab: 'success',
      note: 'default',
      milestone: 'error',
    };
    return colors[type] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {typeof error === 'string' ? error : error?.detail || 'Failed to load patient history'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Treatment History
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Complete timeline of patient care and treatment
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExport}
          >
            Export
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => finalPatientId && dispatch(getPatientDetails(finalPatientId))}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <StyledCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Total Events
                  </Typography>
                  <History color="primary" />
                </Box>
                <Typography variant="h4" fontWeight={600}>
                  {stats.totalItems}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  {stats.trend === 'up' ? (
                    <TrendingUp color="success" fontSize="small" />
                  ) : stats.trend === 'down' ? (
                    <TrendingDown color="error" fontSize="small" />
                  ) : null}
                  <Typography variant="caption" color="text.secondary">
                    {stats.last30Days} in last 30 days
                  </Typography>
                </Box>
              </CardContent>
            </StyledCard>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <StyledCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Consultations
                  </Typography>
                  <Psychology color="primary" />
                </Box>
                <Typography variant="h4" fontWeight={600} color="primary">
                  {stats.consultations}
                </Typography>
              </CardContent>
            </StyledCard>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <StyledCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Medications
                  </Typography>
                  <Medication color="info" />
                </Box>
                <Typography variant="h4" fontWeight={600} color="info.main">
                  {stats.medications}
                </Typography>
              </CardContent>
            </StyledCard>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <StyledCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Assessments
                  </Typography>
                  <Assessment color="warning" />
                </Box>
                <Typography variant="h4" fontWeight={600} color="warning.main">
                  {stats.assessments}
                </Typography>
              </CardContent>
            </StyledCard>
          </motion.div>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search history..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FilterList />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateRange}
                label="Date Range"
                onChange={(e) => setDateRange(e.target.value)}
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="30d">Last 30 Days</MenuItem>
                <MenuItem value="90d">Last 90 Days</MenuItem>
                <MenuItem value="6m">Last 6 Months</MenuItem>
                <MenuItem value="1y">Last Year</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={filterType}
                label="Type"
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="consultation">Consultations</MenuItem>
                <MenuItem value="medication">Medications</MenuItem>
                <MenuItem value="assessment">Assessments</MenuItem>
                <MenuItem value="lab">Lab Results</MenuItem>
                <MenuItem value="note">Notes</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<TimelineIcon />} label="Timeline" />
          <Tab icon={<BarChartIcon />} label="Charts" />
          <Tab icon={<TrendingUp />} label="Progress" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {tabValue === 0 && (
          <motion.div
            key="timeline"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            <StyledCard>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Treatment Timeline
                </Typography>
                <Divider sx={{ mb: 3 }} />
                {historyData.length > 0 ? (
                  <TimelineContainer>
                    {historyData.map((item, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <TimelineItem type={item.type}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getTypeIcon(item.type)}
                              <Typography variant="subtitle1" fontWeight={600}>
                                {item.title || item.reason || item.medication_name || `${item.type} - ${format(new Date(item.date), 'MMM dd, yyyy')}`}
                              </Typography>
                              <Chip
                                label={item.type}
                                size="small"
                                color={getTypeColor(item.type)}
                                sx={{ ml: 1 }}
                              />
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {format(new Date(item.date), 'MMM dd, yyyy hh:mm a')}
                            </Typography>
                          </Box>
                          {item.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {item.description}
                            </Typography>
                          )}
                          {item.diagnosis && (
                            <Chip label={item.diagnosis} size="small" variant="outlined" sx={{ mt: 1, mr: 1 }} />
                          )}
                          {item.status && (
                            <Chip
                              label={item.status}
                              size="small"
                              color={
                                item.status === 'Completed' ? 'success' :
                                item.status === 'Pending' ? 'warning' :
                                'default'
                              }
                              sx={{ mt: 1 }}
                            />
                          )}
                        </TimelineItem>
                      </motion.div>
                    ))}
                  </TimelineContainer>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No history found for the selected filters
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </StyledCard>
          </motion.div>
        )}

        {tabValue === 1 && (
          <motion.div
            key="charts"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Monthly Activity Trend
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <RechartsTooltip />
                        <Legend />
                        <Bar dataKey="consultations" fill={theme.palette.primary.main} />
                        <Bar dataKey="medications" fill={theme.palette.info.main} />
                        <Bar dataKey="assessments" fill={theme.palette.warning.main} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>
          </motion.div>
        )}

        {tabValue === 2 && (
          <motion.div
            key="progress"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            <StyledCard>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Treatment Progress
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Typography variant="body1" color="text.secondary">
                  Progress tracking and outcome measurements coming soon...
                </Typography>
              </CardContent>
            </StyledCard>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default EnhancedPatientHistory;

